/**
 * ContactVerificationPrompt — the blocking "Tarkista yhteystietosi" takeover
 * (US4, T040 / T041).
 *
 * Shown only when the member is due for verification (`state.isDue`) and has
 * not yet resolved it this mount (`!dismissed`). BLOCKING: no `Dialog.Close`,
 * Escape and outside-click are cancelled (no passive dismissal, matching the
 * proto's no-close modal and #74's "modaali pakottaa"). The blocking config is
 * lifted from `OnboardingWizard` (the project's established non-dismissible
 * pattern over the repo `Dialog` primitive — never a direct
 * `@radix-ui/react-dialog` import, per `no-radix-dialog-outside-primitives`).
 *
 * Layout rebuilt to match the proto's `UpdateContactsModal`: a full-viewport
 * takeover (`Dialog.Content variant="takeover"`) laid out as `flex flex-col` —
 * a scrollable, centred `max-w-md` content column (heading + demo disclosure +
 * the single inline-edit card) above a STICKY bottom action bar (`border-t`).
 * The card (`ContactVerificationForm`) and its rows flip read↔edit IN PLACE; the
 * action bar swaps its buttons by mode.
 *
 * Two outcomes:
 *   - choice (read) mode: confirm ("Tiedot ovat ajan tasalla", green/primary)
 *     → recordVerification('confirmed') then dismiss; "Päivitä tietoja"
 *     (outline) → editing mode.
 *   - editing mode: the card rows become inputs; "Tallenna" (primary) submits
 *     (validate → honest "not yet implemented" marker; NO write, NO
 *     recordVerification — 034 US2, FR-006); "Peruuta" (outline) returns to choice.
 *
 * Constitution P7: the profile is now read LIVE (034 US2), but the IS slice
 * models no write, so Save persists nothing and says so plainly rather than
 * implying authoritative persistence (member write-back is the deferred
 * `member-profile-edit` sibling). This surface OMITS any on-surface demo-data
 * disclosure line for visual parity with the proto (which has none); the
 * demo-data fact stays disclosed via the SINGLE sanctioned channel — the
 * `/login` honest-demo disclaimer. (Per D-127 owner-review r1 the per-page "demo
 * data" badges, including the Omat tiedot hub badge, were removed — capability
 * honesty ≠ build-demo disclosure; D-127 supersedes the part of D-111 that
 * leaned on that badge.) `useMemberProfile()` and `useContactVerification()`
 * both do a `React.use()` read (via `useContactEditForm`), so this component
 * mounts under the gate's Suspense boundary.
 *
 * Owned by feature 026-proto-convergence-v2.
 * Tasks: T040, T041
 */

import { type FormEvent, type JSX, useState } from 'react';
import { useTranslate } from '../i18n/useTranslate.js';
import { Button } from '../primitives/Button.js';
import { Dialog } from '../primitives/Dialog.js';
import { ContactVerificationForm } from './ContactVerificationForm.js';
import { useContactEditForm } from './useContactEditForm.js';
import { useContactVerification } from './useContactVerification.js';

type PromptMode = 'choice' | 'editing';

export function ContactVerificationPrompt(): JSX.Element | null {
  const t = useTranslate();
  const { state, recordVerification } = useContactVerification();
  const [mode, setMode] = useState<PromptMode>('choice');
  // Local "resolved this mount" flag. Once the member confirms or saves, the
  // demo port's `isDue` would already read false on a fresh read, but we hold a
  // local flag too so the takeover closes immediately without depending on a
  // re-read of the port.
  const [dismissed, setDismissed] = useState(false);
  // On a successful contact write (035 US1), pair the cadence: record the
  // verification as 'updated' and close the takeover. The write itself persists
  // via `useMemberProfileWriter` inside the form (route-on-read re-fetch on ok).
  const editForm = useContactEditForm({
    onSaved: () => {
      void Promise.resolve(recordVerification('updated')).catch(() => {
        // Do not strand the member if the cadence record rejects; the save landed.
      });
      setDismissed(true);
    },
  });

  if (!state.isDue || dismissed) {
    return null;
  }

  const editing = mode === 'editing';

  const handleConfirm = (): void => {
    // recordVerification may return PromiseLike<void>; swallow a future async
    // rejection so it is not unhandled. The demo records synchronously.
    void Promise.resolve(recordVerification('confirmed')).catch(() => {
      // Do not strand the member on a silent reject; the takeover still closes.
    });
    // Optimistic close (deliberate — D-179): we dismiss on click regardless of
    // the write's fate rather than blocking the member on a transient blip for a
    // yearly nag. A genuinely failed write does not persist server-side, so the
    // member is simply re-prompted next session (the adapter does not cache a
    // failed confirm). Trade-off accepted over a modal-open-with-retry flow.
    setDismissed(true);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    editForm.submit();
  };

  const card = <ContactVerificationForm editing={editing} editForm={editForm} />;

  return (
    <Dialog.Root open modal={true}>
      <Dialog.Content
        variant="takeover"
        // No passive dismissal (US4 / #74 "modaali pakottaa"): Radix fires these
        // before its default close handler; preventDefault cancels the close.
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        // aria-modal is not emitted by Radix Dialog v1.1+ by default; pass it so
        // assistive tech sees the modality directly (mirrors OnboardingWizard).
        aria-modal={true}
        className="flex flex-col"
      >
        {/* Scrollable content column (proto: flex-1 … max-w-md). */}
        <div className="mx-auto w-full max-w-md flex-1 px-4 pb-6 pt-8">
          <Dialog.Title size="hero" className="mb-6">
            {t('profile.verify.title')}
          </Dialog.Title>

          {editing ? (
            <form id="contact-verification-form" onSubmit={handleSubmit} noValidate>
              {card}
              {editForm.submitError !== null ? (
                // Honest, localized write failure (035 US1, FR-007/FR-008): a
                // validation / conflict / temporarily-unavailable message — never
                // a raw error, never a false success (P7 / SC-006).
                <p className="mt-4 text-sm text-destructive" role="alert">
                  {t(editForm.submitError)}
                </p>
              ) : null}
            </form>
          ) : (
            card
          )}
        </div>

        {/* Sticky bottom action bar (proto: sticky bottom-0 … border-t). */}
        <div className="sticky bottom-0 border-t border-border bg-background px-4 py-4">
          <div className="mx-auto w-full max-w-md">
            {editing ? (
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  // Abandon the edit session on cancel so the next edit starts clean
                  // (035 US1): dismissing only the email suggestions used to leave a
                  // failed save's error message rendered, which then read as a fresh
                  // failure when the member re-opened the form (P7).
                  onClick={() => {
                    editForm.reset();
                    setMode('choice');
                  }}
                >
                  {t('profile.verify.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  form="contact-verification-form"
                  className="flex-1"
                  disabled={editForm.isSubmitting}
                >
                  {t('profile.verify.save')}
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <Button type="button" variant="primary" className="w-full" onClick={handleConfirm}>
                  {t('profile.verify.confirm')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => setMode('editing')}
                >
                  {t('profile.verify.startEdit')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
